/**
 * LFB-QAD-SP-004 Rev.07 — ตารางที่ 3 APPEARANCE (Kraft Liner Board)
 *
 * - ต้องไม่มี: ตาไม้, เปื้อนเศษสี, จุด Calender
 * - จุดด่างดำจากเยื่อกระดาษ ในพื้นที่ 1 ตร.ฟุต:
 *   · เส้นผ่านศูนย์กลางไม่เกิน 5 มม.
 *   · จำนวนไม่เกิน 5 จุด
 *   · จุดที่ขนาดเกิน 5 มม. ระยะห่างต่อจุดต้องไม่ต่ำกว่า 8 ซม.
 *
 * Detector บางครั้งติดป้าย soft pulp spot เป็น wood_knot ("ด่าง") —
 * จุดแบบนุ่ม Ø≤5 มม. ถือเป็นจุดด่างดำ (อนุญาตตามเกณฑ์) ไม่ใช่ตาไม้
 */
export function evaluateAppearanceRules(spots, defects, options = {}) {
  const {
    maxSpotDiameterMm = 5,
    largeSpotThresholdMm = 5,
    minDistanceBetweenLargeSpotsCm = 8,
    maxBlackSpotsPerSqFt = 5,
    sampleAreaSqFt = 1,
  } = options;

  const raw = Array.isArray(spots) ? spots : [];
  const defectList = Array.isArray(defects) ? defects : [];

  const list = raw.map((s) => normalizeSpotForTable3(s, maxSpotDiameterMm));

  const oversized = list.filter((s) => Number(s.diameterMm) > maxSpotDiameterMm);
  // PDF: ระยะห่างใช้กับจุดที่ขนาด "เกินกว่า" 5 มม.
  const large = list.filter((s) => Number(s.diameterMm) > largeSpotThresholdMm);
  const distanceViolations = [];
  for (let i = 0; i < large.length; i += 1) {
    for (let j = i + 1; j < large.length; j += 1) {
      const a = large[i];
      const b = large[j];
      if (a.xMm == null || a.yMm == null || b.xMm == null || b.yMm == null) continue;
      const distCm = Math.hypot(a.xMm - b.xMm, a.yMm - b.yMm) / 10;
      if (distCm < minDistanceBetweenLargeSpotsCm) {
        distanceViolations.push({
          a: a.id,
          b: b.id,
          distanceCm: Number(distCm.toFixed(2)),
        });
      }
    }
  }

  const spotCount = list.length;
  const area = Math.max(0.01, Number(sampleAreaSqFt) || 1);
  const areaLabel = Number(area.toFixed(2));
  const spotsPerSqFt = spotCount / area;
  // ตามเอกสาร: ไม่เกิน 5 จุด ต่อ 1 ตร.ฟุต
  const countExceeded = spotsPerSqFt > maxBlackSpotsPerSqFt;

  const zeroTolerance = [
    ...list.filter((s) => s.type === 'wood_knot'),
    ...defectList
      .filter((d) => ['color_stain', 'calender_spot'].includes(d.type))
      .map((d) => ({ ...d })),
  ];
  // unique by id when possible
  const zeroIds = new Set();
  const zeroUnique = [];
  for (const d of zeroTolerance) {
    const key = d.id || d.relatedSpotId || `${d.type}-${d.diameterMm}`;
    if (zeroIds.has(key)) continue;
    zeroIds.add(key);
    zeroUnique.push(d);
  }

  const formatDia = (s) => {
    const mm = Number(s?.diameterMm);
    return Number.isFinite(mm) ? `Ø${mm.toFixed(1)} มม.` : '';
  };

  const failures = [];
  if (zeroUnique.length) {
    const detail = zeroUnique
      .map((d, i) => `จุด ${i + 1} ${formatDia(d)}`.trim())
      .join(', ');
    failures.push({
      code: 'ZERO_TOLERANCE_DEFECT',
      message: `พบตาไม้/เปื้อนเศษสี/จุด Calender ที่ไม่อนุญาต (${detail})`,
      items: zeroUnique,
    });
  }
  if (oversized.length) {
    const detail = oversized
      .map((s, i) => `จุด ${i + 1} ${formatDia(s)}`.trim())
      .join(', ');
    failures.push({
      code: 'SPOT_TOO_LARGE',
      message: `จุดด่างดำเส้นผ่านศูนย์กลางเกิน ${maxSpotDiameterMm} มม. (${detail})`,
      items: oversized,
    });
  }
  if (countExceeded) {
    failures.push({
      code: 'SPOT_COUNT_EXCEEDED',
      message: `จำนวนจุดด่างดำเกิน ${maxBlackSpotsPerSqFt} จุดต่อ 1 ตร.ฟุต (พบ ${spotCount} จุด ในพื้นที่ ~${areaLabel} ตร.ฟุต ≈ ${spotsPerSqFt.toFixed(2)} จุด/ตร.ฟุต)`,
      count: spotCount,
      perSqFt: Number(spotsPerSqFt.toFixed(2)),
    });
  }
  if (distanceViolations.length) {
    failures.push({
      code: 'SPOT_DISTANCE_TOO_CLOSE',
      message: `จุดขนาดใหญ่กว่า ${largeSpotThresholdMm} มม. ระยะห่างน้อยกว่า ${minDistanceBetweenLargeSpotsCm} ซม.`,
      items: distanceViolations,
    });
  }

  return {
    verdict: failures.length ? 'NG' : 'OK',
    spots: list,
    defects: zeroUnique,
    failures,
    sampleAreaSqFt: areaLabel,
    spotCount,
    spotsPerSqFt: Number(spotsPerSqFt.toFixed(2)),
  };
}

/**
 * Soft pulp-like marks Ø≤5mm tagged as wood_knot → จุดด่างดำ (allowed under count/size rules).
 * True wood knots (hard / larger) stay wood_knot → zero tolerance.
 */
function normalizeSpotForTable3(spot, maxSpotDiameterMm) {
  if (!spot || typeof spot !== 'object') return spot;
  const d = Number(spot.diameterMm) || 0;
  const typed = spot.type || (spot.isKnot ? 'wood_knot' : 'black_spot');
  const softPulpMislabel =
    typed === 'wood_knot' &&
    d <= maxSpotDiameterMm &&
    (spot.soft === true || d < 4);

  // Soft / small "ด่าง" from detector = จุดด่างดำ per Table 3, not ตาไม้
  if (softPulpMislabel) {
    return { ...spot, type: 'black_spot', isKnot: false };
  }
  if (typed === 'wood_knot') {
    return { ...spot, type: 'wood_knot', isKnot: true };
  }
  return { ...spot, type: typed || 'black_spot' };
}
